import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import { ConfirmationModal, Text } from '@frontend/design-system';

export type BlockNumberModalMode = 'block' | 'unblock';

const nowrap = { whiteSpace: 'nowrap' } as const;

export type BlockNumberModalProps = {
  show: boolean;
  mode: BlockNumberModalMode;
  phoneNumber: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

export const BlockNumberModal = ({ show, mode, phoneNumber, onClose, onConfirm }: BlockNumberModalProps) => {
  const { t } = useTranslation('calls');
  const formatted = formatPhoneNumber(phoneNumber);

  const copy = {
    block: {
      title: t('Block Phone Number?'),
      confirmLabel: t('Block Number'),
      body: (
        <Trans t={t} parent={Text}>
          <Text as='span' weight='bold' css={nowrap}>
            {{ phoneNumber: formatted }}
          </Text>{' '}
          will no longer be able to call your location(s).
        </Trans>
      ),
      hint: t('You can unblock this phone number anytime from Call Blocking settings under Phone Settings.'),
    },
    unblock: {
      title: t('Unblock Phone Number?'),
      confirmLabel: t('Unblock Number'),
      body: (
        <Trans t={t} parent={Text}>
          You will start receiving phone calls from{' '}
          <Text as='span' weight='bold' css={nowrap}>
            {{ phoneNumber: formatted }}
          </Text>
          .
        </Trans>
      ),
      hint: t('You can manage your blocked numbers from Call Blocking settings under Phone Settings.'),
    },
  }[mode];

  return (
    <ConfirmationModal
      show={show}
      title={copy.title}
      confirmLabel={copy.confirmLabel}
      cancelLabel={t('Cancel')}
      onConfirm={onConfirm}
      onClose={onClose}
      destructive={false}
      maxWidth={400}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(2)};
        `}
      >
        {copy.body}
        {copy.hint}
      </div>
    </ConfirmationModal>
  );
};
